import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/components/AuthProvider';
import { User, Role, UserRole } from '@/types/firebase';
// Update imports to include updateUser and updateUserRole
import { updateUser } from '@/lib/api';
import { updateUserRole } from '@/lib/auth';
import { defaultTemplates } from '@/lib/emailTemplates';
// Removing unused imports but keeping as comments in case they're needed later
// import { createUser, updateUser } from '@/lib/api';
// import { showNotification } from '@/lib/generalNotifications';

// Keeping function but marking as unused with comment
// Helper function that might be needed in the future
// const getBaseUrl = () => {
//   if (typeof window === 'undefined') {
//     // Server-side
//     return import.meta.env?.VITE_API_URL || '';
//   }
//   // Client-side
//   return typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL
//     ? import.meta.env.VITE_API_URL
//     : window.location.origin;
// };

interface UserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user?: User | null;
  roles: Role[];
  onSuccess: () => void;
  // Keeping emailTemplates in interface even if not used currently
  emailTemplates?: Record<string, string>;
}

export function UserDialog({ open, onOpenChange, user, roles, onSuccess /* emailTemplates */ }: UserDialogProps) {
  const { user: currentUser } = useAuth();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roleType, setRoleType] = useState<UserRole>('user');
  const [roleId, setRoleId] = useState<string>('');
  const [isActive, setIsActive] = useState(true);
  // Keep these variables but mark as intentionally unused with underscore prefix
  // They may be needed for future functionality
  const [_isSubmitting, _setIsSubmitting] = useState(false); // Reserved for future use
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [_error, setError] = useState(''); // Reserved for future use
  const [_notification, setNotification] = useState({ type: '', message: '', duration: 0 }); // Reserved for future use
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const isEditMode = !!user;

  // Handle role change when roleId changes
  useEffect(() => {
    if (roleId) {
      // Check if the selected role is an admin role
      const selectedRole = roles.find(r => r.id === roleId);
      if (selectedRole && selectedRole.name.toLowerCase() === 'admin') {
        setRoleType('admin');
      } else if (roleId !== 'none') {
        // For any other role, set to 'user'
        setRoleType('user');
      }
    } else if (roleId === 'none') {
      // Default to 'user' when no specific role is selected
      setRoleType('user');
    }
  }, [roleId, roles]);

  useEffect(() => {
    if (user) {
      // Split display name into first and last name parts
      const nameParts = (user.displayName || '').split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      setEmail(user.email || '');
      setRoleType(user.role || 'user');
      setRoleId(user.roleId || '');
      setIsActive(user.isActive);
    } else {
      // Default values for new user
      setFirstName('');
      setLastName('');
      setEmail('');
      setRoleType('user');
      setRoleId('');
      setIsActive(true);
    }
  }, [user, open]);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to determine correct role type based on roleId
  const determineRoleType = (selectedRoleId: string): UserRole => {
    if (!selectedRoleId) {
      return 'user';
    }
    
    const selectedRole = roles.find(r => r.id === selectedRoleId);
    return selectedRole?.name.toLowerCase() === 'admin' ? 'admin' : 'user';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Ensure roleType is correctly set based on roleId
    const updatedRoleType = determineRoleType(roleId);

    console.log("Submitting form data:", { 
      email, 
      firstName, 
      lastName, 
      roleType: updatedRoleType, 
      roleId
    });

    if (!validate()) {
      setError('Please fill out all required fields');
      setLoading(false);
      return;
    }

    try {
      setIsSubmitting(true);
      
      // For new user (invitation flow)
      if (!user) {
        // Call the API to send an invitation
        const response = await fetch('/api/invitation', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email,
            firstName: firstName || '',
            lastName: lastName || '',
            role: updatedRoleType, // Use the updated role type
            roleId: roleId ? roleId : null,
            companyId: currentUser?.companyId,
            template: defaultTemplates.userInvitation,
            subject: defaultTemplates.userInvitationSubject,
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Failed to send invitation');
        }

        setNotification({
          type: 'success',
          message: `Invitation sent to ${email}. They will receive an email with instructions to join.`,
          duration: 5000
        });
        
        onSuccess();
        onOpenChange(false);
      } else {
        // Check if the role has changed
        const roleChanged = updatedRoleType !== user.role || roleId !== user.roleId;
        
        console.log('Role change data:', {
          current: { role: user.role, roleId: user.roleId },
          new: { role: updatedRoleType, roleId: roleId },
          hasChanged: roleChanged
        });

        if (roleChanged) {
          // Check if this is downgrading the last admin
          const wasAdmin = user.role === 'admin';
          const wasAdminByRole = roles.find(r => r.id === user.roleId)?.name.toLowerCase() === 'admin';
          const willBeAdmin = updatedRoleType === 'admin';
          const willBeAdminByRole = roleId ? roles.find(r => r.id === roleId)?.name.toLowerCase() === 'admin' : false;

          // If changing from admin to non-admin, check if this is the last admin
          if ((wasAdmin || wasAdminByRole) && !willBeAdmin && !willBeAdminByRole) {
            // We'll let the server-side check handle this, but provide good error messaging
            console.log('Warning: Attempting to change role from admin to non-admin');
          }

          // Use the updateUserRole function for role changes to ensure roleId is properly updated
          console.log('Updating user role with:', {
            uid: user.uid,
            roleType: updatedRoleType,
            companyId: currentUser?.companyId || '',
            customRoleId: roleId ? roleId : undefined
          });
          
          try {
            await updateUserRole(
              user.uid, 
              updatedRoleType, 
              currentUser?.companyId || '', 
              roleId ? roleId : undefined
            );
            
            // Update other user fields separately
            await updateUser(user.uid, {
              displayName: `${firstName} ${lastName}`.trim(),
              isActive,
              updatedAt: new Date(),
            });
            
            // Construct a more detailed success message
            let successMessage = 'User updated successfully.';
            
            if (updatedRoleType === 'admin') {
              successMessage += ' Admin user now has full access.';
            } else if ((wasAdmin || wasAdminByRole) && !willBeAdmin && !willBeAdminByRole) {
              // When downgrading from admin to another role
              const selectedRoleName = roleId ? roles.find(r => r.id === roleId)?.name : 'standard user';
              successMessage += ` User has been downgraded from admin to user with ${selectedRoleName} permissions.`;
            } else {
              successMessage += ` User's role changed to ${updatedRoleType === 'user' ? 'user' : 'admin'} with appropriate permissions.`;
            }
            
            setNotification({
              type: 'success',
              message: successMessage,
              duration: 5000
            });
            
            onSuccess();
            onOpenChange(false);
          } catch (roleError) {
            // Handle specific error for last admin user
            if (roleError instanceof Error && roleError.message.includes('last admin user')) {
              setError('Cannot remove admin role from the last admin user. Companies must maintain at least one admin user.');
            } else {
              throw roleError; // Re-throw if it's not the specific error we're handling
            }
          }
        } else {
          // If role hasn't changed, just update the other fields
          await updateUser(user.uid, {
            displayName: `${firstName} ${lastName}`.trim(),
            isActive,
            updatedAt: new Date(),
          });
          
          setNotification({
            type: 'success',
            message: 'User updated successfully',
            duration: 5000
          });
          
          onSuccess();
          onOpenChange(false);
        }
      }
    } catch (error) {
      console.error('Error in UserDialog:', error);
      
      // Show a user-friendly error message
      if (error instanceof Error) {
        // Handle specific known errors
        if (error.message.includes('last admin user')) {
          setError('Cannot remove admin role from the last admin user. Companies must maintain at least one admin user.');
        } else if (error.message.includes('email settings') || error.message.includes('SendGrid')) {
          setError('Email configuration error: ' + error.message);
        } else {
          setError(error.message);
        }
        
        // Show notification for errors that might require admin action
        setNotification({
          type: 'error',
          message: error.message,
          duration: 10000, // Show for longer (10 seconds)
        });
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit User' : 'Invite User'}</DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update the user details and permissions.' 
              : 'Send an invitation to a new user to join your organization.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="firstName" className="text-right">
              First Name
            </Label>
            <Input
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="col-span-3"
              placeholder="First Name"
            />
            {errors.firstName && (
              <div className="col-span-4 text-right text-sm text-red-500">{errors.firstName}</div>
            )}
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="lastName" className="text-right">
              Last Name
            </Label>
            <Input
              id="lastName"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="col-span-3"
              placeholder="Last Name"
            />
            {errors.lastName && (
              <div className="col-span-4 text-right text-sm text-red-500">{errors.lastName}</div>
            )}
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="col-span-3"
              placeholder="user@example.com"
              disabled={isEditMode} // Can't change email for existing users
            />
            {errors.email && (
              <div className="col-span-4 text-right text-sm text-red-500">{errors.email}</div>
            )}
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="role" className="text-right">
              Role
            </Label>
            <div className="col-span-3">
              <Select 
                value={roleId} 
                onValueChange={setRoleId}
                disabled={loading || isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific role</SelectItem>
                  {roles.map(role => {
                    const isAdminRole = role.name.toLowerCase() === 'admin' || role.name.toLowerCase() === 'administrator';
                    const isSystemRole = role.isSystemRole || isAdminRole;
                    
                    return (
                      <SelectItem 
                        key={role.id} 
                        value={role.id}
                        className={isAdminRole ? 'font-bold text-primary' : ''}
                      >
                        {role.name}
                        {isSystemRole && ' (System Role)'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground">
                Effective role type: {roleType === 'admin' ? 'Administrator access' : 'Standard user access'}
              </p>
            </div>
          </div>
          
          {isEditMode && (
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Active
              </Label>
              <div className="flex items-center space-x-2 col-span-3">
                <Switch
                  id="status"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
                <Label htmlFor="status" className="cursor-pointer">
                  {isActive ? 'User is active' : 'User is inactive'}
                </Label>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || isSubmitting}
          >
            {loading || isSubmitting ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-r-foreground" />
                <span>Sending...</span>
              </div>
            ) : isEditMode ? 'Save Changes' : 'Send Invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 
